import React, { useCallback, useMemo, useState } from 'react';
import type Konva from 'konva';
import { Circle, Group, Label, Tag, Text as KonvaText } from 'react-konva';
import { useSimulationTrackNodes } from '../../hooks/useSimulationTrackNodes';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  moveTrackNodeAnnotation,
  selectTrackNode,
  updateElements,
} from '../../features/layout/layoutSlice';
import { checkTrackNodeMove, planTrackNodeMove } from '../../utils/trackNodeMove';
import { clearSelection } from '../../features/selection/selectionSlice';
import { selectInstance } from '../../features/equipment/equipmentSlice';
import { openRightDrawerTab } from '../../features/ui/uiSlice';
import {
  annotationForTrackNode,
  DEFAULT_GATE_FOUP_CAPACITY,
  trackNodeDisplayName,
} from '../../utils/trackNodeAnnotations';
import type { TrackNodeLandmarkRole } from '../../types';
import {
  ALL_RAIL_TYPES,
  ENDPOINT_TOLERANCE,
  isCurveRailType,
  resolveRailType,
} from '../../utils/topology';
import { buildRailGeometryNodeMarkers } from '../../utils/simulationTrackNetworkView';
import {
  resolveDerivedGateModules,
  resolveGate2DGroupPosition,
} from '../../utils/derivedGateEquipment';
import DerivedGateEquipment2D from './DerivedGateEquipment2D';

type Props = {
  zoomLevel?: number;
};

const ROLE_FILL: Record<TrackNodeLandmarkRole, string> = {
  none: '#94a3b8',
  entry: '#0d9488',
  exit: '#e11d48',
  junction: '#6366f1',
};

const ROLE_STROKE: Record<TrackNodeLandmarkRole, string> = {
  none: '#64748b',
  entry: '#0f766e',
  exit: '#be123c',
  junction: '#4338ca',
};

const SimulationTrackNodeOverlay: React.FC<Props> = ({ zoomLevel = 1 }) => {
  const dispatch = useAppDispatch();
  const showTrackNodes = useAppSelector((state) => state.ui.showSimulationTrackNodes);
  const pointDrawActive = useAppSelector((state) => state.ui.pointDraw.active);
  const trackNodePlacementActive = useAppSelector((state) => state.ui.trackNodePlacementActive);
  const selectedTrackNodeId = useAppSelector((state) => state.layout.selectedTrackNodeId);
  const annotations = useAppSelector((state) => state.layout.trackNodeAnnotations);
  const elements = useAppSelector((state) => state.layout.elements);
  const trackResizeActive = useAppSelector((state) =>
    state.selection.selectedIds.some((id) => {
      const element = state.layout.elements[id];
      if (!element || element.metadata.locked || !ALL_RAIL_TYPES.has(element.type)) return false;
      return !isCurveRailType(resolveRailType(element));
    })
  );
  const nodes = useSimulationTrackNodes();
  const gridSettings = useAppSelector((state) => state.ui.gridSettings);
  const activeTool = useAppSelector((state) => state.ui.activeTool);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const elementList = useMemo(() => Object.values(elements), [elements]);

  /**
   * Drag a node to move every straight-track end that meets there. Nodes on a
   * curve, junction or locked rail stay put and say why on hover.
   */
  const nodeDragProps = useCallback(
    (node: { nodeId: string; x: number; y: number }) => {
      const check =
        activeTool === 'select' || activeTool === 'pan'
          ? checkTrackNodeMove(elementList, node)
          : { ok: false, reason: undefined, attached: [] };
      const draggable = check.ok;
      const setCursor = (e: Konva.KonvaEventObject<unknown>, cursor: string) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = cursor;
      };
      const snap = (value: number) => {
        const size = gridSettings.snapGridSize;
        if (!gridSettings.snapToGrid || !(size > 0)) return value;
        return Math.round(value / size) * size;
      };
      return {
        draggable,
        dragReason: check.reason ?? null,
        onMouseEnter: (e: Konva.KonvaEventObject<MouseEvent>) => {
          setHoveredNodeId(node.nodeId);
          setCursor(e, draggable ? 'grab' : 'pointer');
        },
        onMouseLeave: (e: Konva.KonvaEventObject<MouseEvent>) => {
          setHoveredNodeId(null);
          setCursor(e, 'default');
        },
        onDragStart: (e: Konva.KonvaEventObject<DragEvent>) => {
          e.cancelBubble = true;
          setCursor(e, 'grabbing');
        },
        onDragMove: (e: Konva.KonvaEventObject<DragEvent>) => {
          e.cancelBubble = true;
          const group = e.target;
          group.position({ x: snap(group.x()), y: snap(group.y()) });
        },
        onDragEnd: (e: Konva.KonvaEventObject<DragEvent>) => {
          e.cancelBubble = true;
          setCursor(e, 'grab');
          const group = e.target;
          const target = { x: snap(group.x()), y: snap(group.y()) };
          const plan = planTrackNodeMove(elementList, node, target);
          if (plan.updates.length === 0) {
            // Blocked or no movement: put the marker back on its node.
            group.position({ x: node.x, y: node.y });
            return;
          }
          dispatch(updateElements(plan.updates));
          dispatch(moveTrackNodeAnnotation({ from: { x: node.x, y: node.y }, to: target }));
        },
      };
    },
    [activeTool, dispatch, elementList, gridSettings.snapGridSize, gridSettings.snapToGrid]
  );
  const gateModules = useMemo(
    () => resolveDerivedGateModules(Object.values(elements), annotations),
    [annotations, elements]
  );
  const gateModuleByNodeId = useMemo(
    () => new Map(gateModules.map((module) => [module.trackNodeId, module])),
    [gateModules]
  );

  const curveGeometryNodes = useMemo(
    () =>
      buildRailGeometryNodeMarkers(Object.values(elements)).filter((m) => m.role === 'curve-node'),
    [elements]
  );

  const { gateNodes, switchNodes, plainNodes } = useMemo(() => {
    const gates: typeof nodes = [];
    const switches: typeof nodes = [];
    const plain: typeof nodes = [];
    for (const node of nodes) {
      const annotation = annotationForTrackNode(annotations, node);
      const role = annotation.role;
      if (role === 'entry' || role === 'exit') gates.push(node);
      if (role === 'junction' || annotation.isJunction) {
        switches.push(node);
      } else if (role !== 'entry' && role !== 'exit') {
        plain.push(node);
      }
    }
    const tol = ENDPOINT_TOLERANCE;
    const plainDeduped = plain.filter(
      (node) => !curveGeometryNodes.some((g) => Math.hypot(g.x - node.x, g.y - node.y) <= tol)
    );
    return { gateNodes: gates, switchNodes: switches, plainNodes: plainDeduped };
  }, [annotations, nodes, curveGeometryNodes]);

  // Entry/exit stay on even when the node layer is hidden — they are layout
  // landmarks, not optional network chrome.
  if (!showTrackNodes && gateNodes.length === 0) return null;

  const radius = 5 / zoomLevel;
  const strokeWidth = 1.5 / zoomLevel;
  const selectNode = (nodeId: string) => {
    dispatch(clearSelection());
    dispatch(selectInstance(null));
    dispatch(selectTrackNode(nodeId));
    dispatch(openRightDrawerTab('properties'));
  };

  const renderTooltip = (
    node: (typeof nodes)[number],
    annotation: ReturnType<typeof annotationForTrackNode>,
    yOffset: number,
    dragHint: string | null = null
  ) => {
    const roleLabel =
      annotation.role === 'entry'
        ? 'Entry'
        : annotation.role === 'exit'
          ? 'Exit'
          : annotation.role === 'junction'
            ? 'Junction / Switch'
            : null;
    const displayName = trackNodeDisplayName(annotation, node.nodeId);
    const segmentText =
      node.segmentIds.length > 0 ? node.segmentIds.join(', ') : 'No connected segments';
    const capacity =
      typeof annotation.foupCapacity === 'number' && annotation.foupCapacity > 0
        ? annotation.foupCapacity
        : annotation.role === 'entry' || annotation.role === 'exit'
          ? DEFAULT_GATE_FOUP_CAPACITY
          : null;
    const tooltipLines = [
      displayName,
      roleLabel,
      capacity != null ? `FOUP capacity: ${capacity}` : null,
      node.isHoldingPoint ? 'Holding point' : null,
      `Tracks: ${segmentText}`,
      dragHint ?? 'Drag to move the connected track ends',
    ]
      .filter(Boolean)
      .join('\n');

    return (
      <Label y={yOffset} listening={false}>
        <Tag
          fill="rgba(17, 24, 39, 0.92)"
          pointerDirection="down"
          pointerWidth={8 / zoomLevel}
          pointerHeight={6 / zoomLevel}
          cornerRadius={4 / zoomLevel}
        />
        <KonvaText
          text={tooltipLines}
          fill="#fef9c3"
          fontSize={11 / zoomLevel}
          padding={6 / zoomLevel}
          align="center"
        />
      </Label>
    );
  };

  return (
    <Group
      // Nodes sit at track ends, directly over the resize handles. Make the
      // overlay non-interactive while resize chrome is visible so pointer
      // events reach the handles underneath.
      listening={!pointDrawActive && !trackNodePlacementActive && !trackResizeActive}
    >
      {gateNodes.map((node) => {
        const hovered = !trackResizeActive && hoveredNodeId === node.nodeId;
        const selected = selectedTrackNodeId === node.nodeId;
        const annotation = annotationForTrackNode(annotations, node);
        const module = gateModuleByNodeId.get(node.nodeId);
        if (!module) return null;
        const scale = hovered || selected ? 1.1 : 1;
        const groupPos = resolveGate2DGroupPosition(module);

        return (
          <Group
            key={node.nodeId}
            x={groupPos.x}
            y={groupPos.y}
            scaleX={scale}
            scaleY={scale}
            onMouseEnter={(e) => {
              setHoveredNodeId(node.nodeId);
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'pointer';
            }}
            onMouseLeave={(e) => {
              setHoveredNodeId(null);
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'default';
            }}
            onClick={(e) => {
              e.cancelBubble = true;
              selectNode(node.nodeId);
            }}
            onTap={(e) => {
              e.cancelBubble = true;
              selectNode(node.nodeId);
            }}
          >
            <DerivedGateEquipment2D
              module={module}
              groupX={groupPos.x}
              groupY={groupPos.y}
              selected={selected}
              zoomLevel={zoomLevel}
            />
            {hovered && renderTooltip(node, annotation, -(12 + 6 / zoomLevel))}
          </Group>
        );
      })}

      {showTrackNodes &&
        switchNodes.map((node) => {
          const hovered = !trackResizeActive && hoveredNodeId === node.nodeId;
          const selected = selectedTrackNodeId === node.nodeId;
          const annotation = annotationForTrackNode(annotations, node);
          const { dragReason, ...dragProps } = nodeDragProps(node);
          const fill = ROLE_FILL.junction;
          const stroke = selected ? '#1d4ed8' : ROLE_STROKE.junction;
          const r = (hovered || selected ? radius * 1.8 : radius * 1.45) * 1.15;

          return (
            <Group
              key={`j-${node.nodeId}`}
              x={node.x}
              y={node.y}
              onClick={(e) => {
                e.cancelBubble = true;
                selectNode(node.nodeId);
              }}
              onTap={(e) => {
                e.cancelBubble = true;
                selectNode(node.nodeId);
              }}
              {...dragProps}
            >
              <Circle
                radius={r}
                fill={fill}
                stroke={stroke}
                strokeWidth={selected ? strokeWidth * 1.6 : strokeWidth}
                opacity={0.92}
                perfectDrawEnabled={false}
                hitStrokeWidth={12 / zoomLevel}
              />
              <KonvaText
                text="J"
                x={-r}
                y={-r}
                width={r * 2}
                height={r * 2}
                align="center"
                verticalAlign="middle"
                fill="#ffffff"
                fontSize={Math.max(4, (r * 1.15) / zoomLevel)}
                fontStyle="bold"
                listening={false}
                perfectDrawEnabled={false}
              />
              {hovered && renderTooltip(node, annotation, -(r + 10 / zoomLevel), dragReason)}
            </Group>
          );
        })}

      {showTrackNodes &&
        curveGeometryNodes.map((marker) => (
          <Circle
            key={`curve-geo-${marker.railId}-${marker.index}`}
            x={marker.x}
            y={marker.y}
            radius={radius * 0.9}
            fill={ROLE_FILL.none}
            stroke={ROLE_STROKE.none}
            strokeWidth={strokeWidth}
            listening={false}
            perfectDrawEnabled={false}
          />
        ))}

      {showTrackNodes &&
        plainNodes.map((node) => {
          const hovered = !trackResizeActive && hoveredNodeId === node.nodeId;
          const selected = selectedTrackNodeId === node.nodeId;
          const annotation = annotationForTrackNode(annotations, node);
          const { dragReason, ...dragProps } = nodeDragProps(node);
          const fill = ROLE_FILL[annotation.role];
          const stroke = selected ? '#1d4ed8' : ROLE_STROKE[annotation.role];

          return (
            <Group
              key={node.nodeId}
              x={node.x}
              y={node.y}
              onClick={(e) => {
                e.cancelBubble = true;
                selectNode(node.nodeId);
              }}
              onTap={(e) => {
                e.cancelBubble = true;
                selectNode(node.nodeId);
              }}
              {...dragProps}
            >
              <Circle
                radius={hovered || selected ? radius * 1.35 : radius}
                fill={fill}
                stroke={stroke}
                strokeWidth={selected ? strokeWidth * 1.6 : strokeWidth}
                perfectDrawEnabled={false}
                hitStrokeWidth={12 / zoomLevel}
              />
              {hovered && renderTooltip(node, annotation, -(radius + 10 / zoomLevel), dragReason)}
            </Group>
          );
        })}
    </Group>
  );
};

export default React.memo(SimulationTrackNodeOverlay);
